import type { FunctionComponent, JSX } from "preact";
import { useState } from "preact/hooks";
import { keepPreviousData, useQuery } from "@tanstack/preact-query";
import Spinner from "./Spinner";
import type { Collection } from "../tests-api.ts";
import * as stylex from "@stylexjs/stylex";
import Button from "./Button.tsx";

import moeru from "../assets/moeru.png";

const styles = stylex.create({
    fetchControls: {
        gap: 15,
        display: "flex",
        width: "100%",
        justifyContent: "center",
    },
    emptyList: {
        gap: 5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        fontSize: 16,
        marginBottom: 20,
        fontFamily: "'Inter Variable', sans-serif",
        filter: "grayscale(1) opacity(.6)",
    },
});

const EmptyListPlaceholder: FunctionComponent = () => (
    <div {...stylex.props(styles.emptyList)}>
        <img alt="Тут ничего нет..." width={150} src={moeru} draggable={false} />
        Тут ничего нет...
    </div>
);

interface OffsetFetchProps<T> {
    queryKey: string[];
    queryFn: (offset: string) => Promise<Collection<T>>;
    item: (data: T, offset: string) => JSX.Element;
    onEmpty?: () => JSX.Element;
    Wrapper?: (props: { children: JSX.Element | (JSX.Element | null)[] }) => JSX.Element;
    loadingLabel?: string;
    initialOffset?: string;
}

export default function OffsetFetch<T>({
    queryKey,
    queryFn,
    item,
    onEmpty = () => <EmptyListPlaceholder />,
    Wrapper = ({ children }) => <>{children}</>,
    initialOffset = "0",
    loadingLabel = "Загрузка",
}: OffsetFetchProps<T>): JSX.Element {
    const [offsets, setOffsets] = useState<string[]>([initialOffset]);
    const { data, error, isPlaceholderData, isLoading, isRefetching, isError } = useQuery<
        Collection<any>
    >({
        queryKey: [...queryKey, "offset", offsets[offsets.length - 1]],
        queryFn: () => queryFn(offsets[offsets.length - 1]),
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
    });

    if (isError) return <span style={{ color: "red" }}>{error}</span>;

    if (isLoading || isRefetching || !data) return <Spinner.Loader label={loadingLabel} />;

    if (!data.list.length) return onEmpty();

    return (
        <>
            <Wrapper>
                <>{data!.list.map((i) => item(i, offsets[offsets.length - 1]))}</>
            </Wrapper>

            <div {...stylex.props(styles.fetchControls)}>
                {offsets.length > 1 ? (
                    <Button
                        label={isPlaceholderData ? "Загрузка..." : "Назад"}
                        kind="miniDefault"
                        disabled={isPlaceholderData}
                        onClick={() => setOffsets((o) => o.slice(0, -1))}
                    />
                ) : null}

                {data!.hasMore ? (
                    <Button
                        label={isPlaceholderData ? "Загрузка..." : "Дальше"}
                        kind="miniDefault"
                        disabled={isPlaceholderData}
                        onClick={() => setOffsets((o) => [...o, data!.nextOffset])}
                    />
                ) : null}
            </div>
        </>
    );
}
